<!-- Live stream binding options (Phase 3 editor-template field rows). -->

<template>
  <div class="field-content lsb-fields">
    <!-- TODO: think about adding below-field error message & error state indication (e.g. red border) [DSP-N26CxI4P] -->

    <!-- Stream Type -->
    <div class="d-flex field-row">
      <label class="basis-1/2 text-sm font-bold">Stream Type</label>
      <SelectField
        class="basis-1/2 lsb-select"
        :options="streamTypeOptions"
        v-model="streamType"
        label-key="label"
        value-key="name"
        test-id="live-stream-type"
      />
    </div>

    <!-- Stream URL -->
    <div class="d-flex field-row">
      <label for="lsb-stream-url" class="basis-1/2 text-sm font-bold">Stream URL &#9733;</label>
      <TextField
        id="lsb-stream-url"
        name="streamUrl"
        data-test-id="live-stream-url"
        v-model="streamUrl"
        class="basis-1/2"
        width="100%"
        maxlength="2000"
      />
    </div>

    <template v-if="streamType !== 'webpage'">
      <!-- Start Time -->
      <div class="d-flex field-row">
        <label class="basis-1/2 text-sm font-bold">Start Time</label>
        <div class="basis-1/2 lsb-pair">
          <DateField v-model="startDate" data-test-id="live-stream-start-date" disabled width="100%" />
          <TimeField v-model="startTime" data-test-id="live-stream-start-time" disabled width="100%" step=1 />
        </div>
      </div>

      <!-- End Time -->
      <div class="d-flex field-row">
        <label class="basis-1/2 text-sm font-bold">End Time</label>
        <div class="basis-1/2 lsb-pair">
          <DateField v-model="endDate" data-test-id="live-stream-end-date" width="100%" />
          <TimeField v-model="endTime" data-test-id="live-stream-end-time" width="100%" step=1 />
        </div>
      </div>

      <!-- Stream Duration -->
      <div class="d-flex field-row">
        <label class="basis-1/2 text-sm font-bold">Stream Duration</label>
        <div class="basis-1/2 lsb-duration" data-testid="live-stream-duration">{{ streamDuration }}</div>
      </div>
    </template>
  </div>
</template>


<script setup lang="ts">
import TextField from "@/components/form-fields/TextField.vue";
import SelectField from "@/components/form-fields/SelectField/SelectField.vue";
import DateField from "@/components/form-fields/DateField.vue";
import TimeField from "@/components/form-fields/TimeField.vue";
import sharedRefs from './sharedRefs';

import { useLiveStream } from "./composables/liveStream";
import { computed, ref, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  }
})
const emit = defineEmits(['update:modelValue']);

const {
  streamTypeOptions,
  bindingOptionsInitialValues,
  getStreamDuration
} = useLiveStream()

const streamType = ref(props.modelValue.streamType || bindingOptionsInitialValues.streamType);
const streamUrl = ref(props.modelValue.streamUrl || bindingOptionsInitialValues.streamUrl);
const startDate = ref(props.modelValue.startDate || bindingOptionsInitialValues.startDate);
const startTime = ref(props.modelValue.startTime || bindingOptionsInitialValues.startTime);
const endDate = ref(props.modelValue.endDate || bindingOptionsInitialValues.endDate);
const endTime = ref(props.modelValue.endTime || bindingOptionsInitialValues.endTime);
const { isStartStream } = sharedRefs;
isStartStream.value = props.modelValue.isStartStream || bindingOptionsInitialValues.isStartStream;

const streamDuration = computed(() => getStreamDuration(
  startDate.value,
  startTime.value,
  endDate.value,
  endTime.value
));

// Preparing Binding Data
const bindingOptions = computed(() => {
  return {
    id: props.modelValue.id,
    streamType: streamType.value,
    streamUrl: streamUrl.value,
    startDate: startDate.value,
    startTime: startTime.value,
    endTime: endTime.value,
    endDate: endDate.value,
    isStartStream: isStartStream.value,
  }
});

watch([
  streamType,
  streamUrl,
  startDate,
  startTime,
  endDate,
  endTime,
  isStartStream
], () => {
  emit('update:modelValue', bindingOptions.value)
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/bui-editor-template';

.lsb-fields {
  max-width: 40rem;
}

.lsb-pair {
  display: flex;
  gap: 0.75rem;
  min-width: 0;

  :deep(.field) {
    flex: 1 1 0;
    min-width: 0;
  }

  :deep(input) {
    width: 100% !important;
  }
}

.lsb-duration {
  font-size: 0.875rem;
  color: var(--bui-tbl-row-text, #2a2a30);
}

/* Legacy SelectField shell → tokens. */
.lsb-select {
  :deep(.styled-select) {
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
    border-radius: var(--bui-ter-radius, 8px);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));

    select {
      color: var(--bui-ter-input-text, #1a1a1e);
    }
  }
}

/* Date/time atoms inside pairs. */
.field-content {
  :deep(input[type='date']),
  :deep(input[type='time']) {
    color: var(--bui-ter-input-text, #1a1a1e);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
  }
}
</style>
